import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { useHeaderContext } from '@jouzen/ecom-components/layout';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { useReducedMotion } from 'framer-motion';
import { memo, type RefObject, useEffect, useRef, useState } from 'react';

import Image from '@/app/components/Image';
import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import { type VideoProps } from '@/app/components/Video/types/VideoProps';
import Video from '@/app/components/Video/Video';

import useBackgroundScrollAnimate from './hooks/useBackgroundScrollAnimate';
import type { PopAccuracyComfortSectionProps } from './PopAccuracyComfortSection';
import type { PopAccuracyComfortSectionTheme } from './types';

export interface PopAccuracyComfortSectionBgProps
  extends Pick<
    PopAccuracyComfortSectionProps,
    | 'contentIntroTransitionDelay'
    | 'index'
    | 'isActive'
    | 'shouldScrollAnimateBg'
    | 'theme'
    | 'videoBg'
  > {
  readonly onSectionVisibilityChange: (visible: boolean) => void;
}

export const getMobileBackgroundGradientStyle = (
  theme: PopAccuracyComfortSectionTheme,
): string => {
  const color = theme === 'light' ? '#EFE6DB' : '#4A4741';

  return [
    `linear-gradient(180deg, ${color} 0%, transparent 25%)`,
    `linear-gradient(0deg, ${color} 25%, transparent 40%)`,
  ].join(',');
};

export const getDesktopBackgroundGradientStyle = (
  theme: PopAccuracyComfortSectionTheme,
): string => {
  const gradients =
    theme === 'dark'
      ? [
          `linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, transparent 35.82%)`,
          `linear-gradient(279.21deg, transparent 22.87%, rgba(0, 0, 0, 0.6) 76.56%)`,
        ]
      : [
          `linear-gradient(0deg, #EDE4D8 0%, transparent 35.82%)`,
          `linear-gradient(70.44deg, #EDE4D8 28.65%, transparent 46.42%)`,
          `linear-gradient(56.69deg, rgba(237, 228, 216, 0.6) 1.47%, transparent 39.46%)`,
          `linear-gradient(180deg, rgba(237, 228, 216, 0.6) 0%, transparent 35.82%)`,
        ];
  return gradients.join(',');
};

const getIsSectionVisible = ({
  fullyScrollAnimated,
  isActive,
  shouldReduceMotion,
  shouldScrollAnimateBg,
}: {
  fullyScrollAnimated: boolean;
  isActive: boolean;
  shouldReduceMotion: boolean;
  shouldScrollAnimateBg: boolean;
}): boolean =>
  Boolean(shouldReduceMotion) ||
  (((shouldScrollAnimateBg && fullyScrollAnimated) || !shouldScrollAnimateBg) &&
    isActive);

const PopAccuracyComfortSectionBgVideo = memo(
  function PopAccuracyComfortSectionBgVideo({ ...props }: VideoProps) {
    return <Video {...props} />;
  },
);

const PopAccuracyComfortSectionBg = ({
  contentIntroTransitionDelay = {
    sm: 0.25,
    md: 0.75,
  },
  index,
  isActive,
  onSectionVisibilityChange,
  shouldScrollAnimateBg,
  theme,
  videoBg,
}: PopAccuracyComfortSectionBgProps) => {
  const headerContext = useHeaderContext();
  const setInverse = headerContext?.setInverse;
  const shouldReduceMotion = useReducedMotion();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const isMediumScreen = useMediaQuery(`(min-width: ${BREAKPOINTS.medium}px)`);
  const {
    scrollYProgress: scrollAnimateYProgress,
    style: scrollAnimateStyles,
  } = useBackgroundScrollAnimate({
    ref: wrapperRef as RefObject<HTMLDivElement>,
    shouldScrollAnimate: shouldReduceMotion === false && shouldScrollAnimateBg,
  });
  const fullyScrollAnimated = scrollAnimateYProgress === 1;
  const [videoCurrentTime, setVideoCurrentTime] = useState<number>();

  const isSectionVisible = getIsSectionVisible({
    fullyScrollAnimated,
    isActive,
    shouldReduceMotion: Boolean(shouldReduceMotion),
    shouldScrollAnimateBg,
  });

  const introTransitionDelay =
    contentIntroTransitionDelay[isMediumScreen ? 'md' : 'sm'];

  useEffect(() => {
    if (isSectionVisible) {
      if (!playVideo) {
        setPlayVideo(true);
        setVideoCurrentTime(undefined);
      }
      setInverse?.(theme.md === 'dark' && Boolean(isMediumScreen));
    } else {
      if (playVideo) {
        setPlayVideo(false);
        setVideoCurrentTime(0);
      }
    }
    onSectionVisibilityChange(isSectionVisible);
  }, [
    isSectionVisible,
    isMediumScreen,
    playVideo,
    theme,
    onSectionVisibilityChange,
    setInverse,
  ]);

  return (
    <Motion
      className="relative z-10 overflow-hidden md:absolute md:top-0 md:w-full"
      data-cy="pop-accuracy-comfort-section-bg"
      ref={wrapperRef}
      style={scrollAnimateStyles}
      suppressHydrationWarning
    >
      <Motion
        className="relative w-screen motion-safe:h-[80vh] motion-reduce:h-[120vw] md:h-screen"
        data-cy="pop-accuracy-comfort-section-bg-scale-eq"
        style={{
          backfaceVisibility: 'hidden',
          scale: 1 / Number(scrollAnimateStyles.scale),
        }}
        suppressHydrationWarning
      >
        <Motion
          animate={{ opacity: isActive || shouldReduceMotion ? 1 : 0 }}
          className="size-full"
          data-cy="pop-accuracy-comfort-section-bg-video-frame"
          initial={{
            filter:
              !shouldReduceMotion && index === 0 ? 'blur(5px)' : 'blur(0px)',
          }}
          transition={{ duration: isMediumScreen ? 0 : 1, type: 'tween' }}
          viewport={{ amount: 0.5 }}
          whileInView={{ filter: 'blur(0px)' }}
        >
          <ArtDirectionImage
            className="hidden object-cover motion-reduce:block"
            fill
            {...videoBg.fallback}
          />
          <div className="absolute top-[-25vw] hidden size-full motion-safe:block md:top-0">
            <Image className="object-cover" fill src={videoBg.poster} />
            <PopAccuracyComfortSectionBgVideo
              autoPlay
              className="absolute size-full object-cover"
              contentTitle=""
              controls={false}
              currentTime={videoCurrentTime}
              location={`pop_gen4_accuracy_comfort_section_${index + 1}`}
              muted
              playsInline
              playVideo={playVideo}
              priority
              srcSet={videoBg.srcSet}
              type="video/mp4"
            />
          </div>
          <Motion
            animate={{ opacity: isSectionVisible ? 1 : 0 }}
            aria-hidden="true"
            className="absolute z-10 size-full"
            data-cy="pop-accuracy-comfort-section-bg-gradients"
            initial={{ opacity: 0 }}
            transition={{
              delay: isSectionVisible ? introTransitionDelay : 0,
              duration: 0.75,
              type: 'tween',
            }}
          >
            <div
              className="absolute size-full md:hidden"
              data-cy="pop-accuracy-comfort-section-bg-gradient-mobile"
              style={{
                backgroundImage: getMobileBackgroundGradientStyle(theme.sm),
              }}
            />
            <div
              className="absolute hidden size-full md:block"
              data-cy="pop-accuracy-comfort-section-bg-gradient-desktop"
              style={{
                backgroundImage: getDesktopBackgroundGradientStyle(theme.md),
              }}
            />
          </Motion>
        </Motion>
      </Motion>
    </Motion>
  );
};

export default PopAccuracyComfortSectionBg;
